import type { TFunction } from "i18next";
import type { ItemType } from "@/components/DynamicForm";
import type { PersonnelInfo } from "../types";
import formStyles from "@/components/DynamicForm/index.module.less";
import { getCurrentDate } from "@/utils/common";
import dayjs from "dayjs";
import FileFormField from "@/pages/supplier/SupplierException/components/FileFormField";
import SolutionFileDisplay from "../components/SolutionFileDisplay";

// ==================== 配置函数 ====================

/**
 * 获取人员信息表单项配置
 * 返回人员信息相关的表单项配置数组
 *
 * 注意：表单项配置中定义的是 UI 结构和约束，
 * 实际的表单数据值由主组件通过 formValue 填充
 *
 * @param t - 翻译函数
 * @param personnelInfo - 人员信息对象（可选）
 * @param businessStatus - 业务状态（可选，用于条件显示）
 * @param readOnly - 是否只读模式
 * @param handleOpenSupplierModal - 打开供应商选择弹框的回调函数
 * @returns 表单项配置数组
 */
export const getPersonnelFormItems = (
  t: TFunction,
  personnelInfo?: PersonnelInfo | null,
  businessStatus?: number,
  readOnly: boolean = false,
  handleOpenSupplierModal?: () => void,
): ItemType[] => {
  // 风险类型选项
  const RISK_TYPE_OPTIONS = [
    { label: t("质量风险"), value: "A" },
    { label: t("交付风险"), value: "B" },
    { label: t("财务风险"), value: "C" },
    { label: t("成本风险"), value: "D" },
    { label: t("注册风险"), value: "E" },
    { label: t("其他风险"), value: "F" },
  ];

  // 风险等级评估选项
  const RISK_LEVEL_ASSESSMENT_OPTIONS = [
    { label: t("高"), value: "1" },
    { label: t("中"), value: "2" },
    { label: t("低"), value: "3" },
  ];

  return [
    // ==================== 人员信息 ====================
    {
      name: "personnelWrapperStart",
      col: { span: 24 },
      wrapperStart: { className: formStyles.wrapperPadded },
    },
    {
      label: t("员工姓名"),
      name: "purchaseUserNickname",
      col: { span: 8 },
      labelCol: { span: 24 },
      wrapperCol: { span: 20 },
      widget: {
        widgetType: "Input",
        disabled: true,
        placeholder: t("请输入员工姓名"),
      },
    },
    {
      label: t("员工工号"),
      name: "purchaseUserUsername",
      col: { span: 8 },
      labelCol: { span: 24 },
      wrapperCol: { span: 20 },
      widget: {
        widgetType: "Input",
        disabled: true,
        placeholder: t("请输入员工工号"),
      },
    },
    {
      label: t("创建日期"),
      name: "createTime",
      col: { span: 8 },
      labelCol: { span: 24 },
      wrapperCol: { span: 20 },
      widget: {
        widgetType: "Input",
        disabled: true,
        placeholder: t("请输入创建日期"),
      },
    },
    {
      label: t("部门"),
      name: "purchaseUserDeptName",
      col: { span: 8 },
      labelCol: { span: 24 },
      wrapperCol: { span: 20 },
      widget: {
        widgetType: "Input",
        disabled: true,
        placeholder: t("请输入部门"),
      },
    },
    {
      label: t("岗位"),
      name: "purchaseUserPostName",
      col: { span: 8 },
      labelCol: { span: 24 },
      wrapperCol: { span: 20 },
      widget: {
        widgetType: "Input",
        disabled: true,
        placeholder: t("请输入岗位"),
      },
    },
    {
      label: t("邮箱"),
      name: "purchaseUserEmail",
      col: { span: 8 },
      labelCol: { span: 24 },
      wrapperCol: { span: 20 },
      widget: {
        widgetType: "Input",
        placeholder: t("请输入邮箱"),
      },
    },
    {
      name: "purchaserUserId",
      col: { span: 0 },
      widget: {
        widgetType: "Input",
        hidden: true,
      },
    },
    {
      name: "purchaseUserDeptNo",
      col: { span: 0 },
      widget: {
        widgetType: "Input",
        hidden: true,
      },
    },
    {
      name: "purchaseUserPostNo",
      col: { span: 0 },
      widget: {
        widgetType: "Input",
        hidden: true,
      },
    },
    {
      name: "srmSupplierFlowBusinessFiles",
      col: { span: 0 },
      widget: {
        widgetType: "Input",
        hidden: true,
      },
    },

    {
      name: "personnelWrapperEnd",
      col: { span: 24 },
      wrapperEnd: true,
    },

    {
      name: "supplierSection",
      col: { span: 24 },
      custom: {
        render: () => (
          <div className={formStyles.sectionHeader}>
            <div className={formStyles.sectionIndicator} />
            <span className={formStyles.sectionTitle}>{t("供应商信息")}</span>
          </div>
        ),
      },
    },
    {
      name: "supplierWrapperStart",
      col: { span: 24 },
      wrapperStart: { className: formStyles.wrapperLight },
    },
    {
      label: t("供应商编号"),
      name: "supplierUserCode",
      col: { span: 8 },
      labelCol: { span: 24 },
      wrapperCol: { span: 20 },
      rules: [{ required: true, message: t("供应商编号必填") }],
      widget: {
        widgetType: "Input",
        placeholder: t("请点击选择供应商"),
        readOnly: true,
        style: { cursor: "pointer" },
        onClick: () => handleOpenSupplierModal?.(),
      },
    },
    {
      name: "supplierUserId",
      col: { span: 0 },
      widget: {
        widgetType: "Input",
        hidden: true,
      },
    },
    {
      label: t("供应商名称中文"),
      name: "supplierUserName",
      col: { span: 8 },
      labelCol: { span: 24 },
      wrapperCol: { span: 20 },
      rules: [{ required: true, message: t("供应商名称中文必填") }],
      widget: {
        widgetType: "Input",
        disabled: readOnly,
        placeholder: t("请选择"),
        readOnly: !readOnly,
        style: { cursor: !readOnly ? "pointer" : "default" },
        onClick: !readOnly ? () => handleOpenSupplierModal?.() : undefined,
      },
    },
    {
      label: t("供应商名称英文"),
      name: "supplierUsUserName",
      col: { span: 8 },
      labelCol: { span: 24 },
      wrapperCol: { span: 20 },
      rules: [{ required: true, message: t("供应商名称英文必填") }],
      widget: {
        widgetType: "Input",
        disabled: readOnly,
        placeholder: t("请选择"),
        readOnly: !readOnly,
        style: { cursor: !readOnly ? "pointer" : "default" },
        onClick: !readOnly ? () => handleOpenSupplierModal?.() : undefined,
      },
    },
    {
      label: t("物料类别"),
      name: "materialCategoryValue",
      col: { span: 8 },
      labelCol: { span: 24 },
      wrapperCol: { span: 20 },
      rules: [{ required: true, message: t("物料类别必填") }],
      widget: {
        widgetType: "Select",
        disabled: readOnly,
        placeholder: t("由供应商编号自动填写"),
        options: [],
      },
    },
    {
      label: t("二级物料类别"),
      name: "materialTwoCategoryValue",
      col: { span: 8 },
      labelCol: { span: 24 },
      wrapperCol: { span: 20 },
      rules: [{ required: true, message: t("二级物料类别必填") }],
      widget: {
        widgetType: "Select",
        disabled: readOnly,
        placeholder: t("由供应商编号自动填写"),
        options: [],
      },
    },
    {
      label: t("物料编号"),
      name: "materialCode",
      col: { span: 8 },
      labelCol: { span: 24 },
      wrapperCol: { span: 20 },
      widget: {
        widgetType: "Select",
        placeholder: t("请输入物料编号搜索"),
        allowClear: true,
        filterOption: false,
        options: [],
      },
    },
    {
      label: t("物料名称"),
      name: "materialName",
      col: { span: 8 },
      labelCol: { span: 24 },
      wrapperCol: { span: 20 },
      rules: [{ required: true, message: t("物料名称必填") }],
      widget: {
        widgetType: "Input",
        disabled: readOnly,
        placeholder: t("由供应商编号自动填写"),
      },
    },
    {
      label: t("风险类型"),
      name: "riskTypeValue",
      col: { span: 8 },
      labelCol: { span: 24 },
      wrapperCol: { span: 20 },
      rules: [{ required: true, message: t("风险类型必填") }],
      widget: {
        widgetType: "Select",
        placeholder: t("请选择风险类型"),
        options: RISK_TYPE_OPTIONS,
      },
    },
    {
      name: "riskTypeName",
      col: { span: 0 },
      widget: {
        widgetType: "Input",
        hidden: true,
      },
    },
    {
      label: t("风险等级评估"),
      name: "riskLevelAssessmentValue",
      col: { span: 8 },
      labelCol: { span: 24 },
      wrapperCol: { span: 20 },
      rules: [{ required: true, message: t("风险等级评估必填") }],
      widget: {
        widgetType: "Select",
        placeholder: t("请选择风险等级评估"),
        options: RISK_LEVEL_ASSESSMENT_OPTIONS,
      },
    },
    {
      name: "riskLevelAssessmentName",
      col: { span: 0 },
      widget: {
        widgetType: "Input",
        hidden: true,
      },
    },
    {
      label: t("发现日期"),
      name: "discoveryDate",
      col: { span: 8 },
      labelCol: { span: 24 },
      wrapperCol: { span: 20 },
      initialValue: dayjs(getCurrentDate()),
      rules: [{ required: true, message: t("发现日期必填") }],
      widget: {
        widgetType: "DatePicker",
        placeholder: t("请选择发现日期"),
        format: "YYYY-MM-DD",
        style: { width: "100%" },
      },
    },
    {
      label: t("期望解决日期"),
      name: "expectedResolutionDate",
      col: { span: 8 },
      labelCol: { span: 24 },
      wrapperCol: { span: 20 },
      rules: [{ required: true, message: t("期望解决日期必填") }],
      widget: {
        widgetType: "DatePicker",
        placeholder: t("请选择期望解决日期"),
        format: "YYYY-MM-DD",
        style: { width: "100%" },
      },
    },
    {
      label: t("风险描述"),
      name: "riskDescription",
      col: { span: 24 },
      labelCol: { span: 24 },
      wrapperCol: { span: 20 },
      rules: [{ required: true, message: t("请输入风险描述") }],
      widget: {
        widgetType: "Input.TextArea",
        placeholder: t("请输入"),
        rows: 3,
        maxLength: 500,
        showCount: true,
        style: { resize: "none" },
      },
    },
    {
      label: t("相关证明文件"),
      name: "fileIds",
      col: { span: 24 },
      labelCol: { span: 24 },
      wrapperCol: { span: 24 },
      custom: {
        render: (
          _fieldName: any,
          _remove: any,
          fieldValue: any,
          _context: any,
        ) => (
          <FileFormField
            fieldName="fileIds"
            initialFiles={fieldValue || []}
            required={true}
            readOnly={readOnly}
            fileType={5}
          />
        ),
      },
    },

    // ==================== 供应商解决方案（仅在 businessStatus=3 时显示） ====================
    ...(businessStatus === 3
      ? [
          {
            name: "solutionSection",
            col: { span: 24 },
            custom: {
              render: () => (
                <div className={formStyles.subSectionTitle}>
                  {t("供应商解决方案")}
                </div>
              ),
            },
          } as ItemType,
          {
            name: "solutionWrapperStart",
            col: { span: 24 },
            wrapperStart: { className: formStyles.wrapperLight },
          } as ItemType,
          {
            name: "solutionFiles",
            col: { span: 24 },
            labelCol: { span: 24 },
            wrapperCol: { span: 24 },
            custom: {
              render: (
                _fieldName: any,
                _remove: any,
                _fieldValue: any,
                _context: any,
              ) => {
                // 从表单中获取原始数据，提取 srmSupplierFlowBusinessFiles
                // 注意：这里需要从外部传入 formRef 来获取数据
                return <SolutionFileDisplay files={[]} />;
              },
            },
          } as ItemType,
          {
            label: t("备注说明"),
            name: "remarks",
            col: { span: 24 },
            labelCol: { span: 24 },
            wrapperCol: { span: 20 },
            widget: {
              widgetType: "Input.TextArea",
              placeholder: t("请输入备注说明"),
              rows: 3,
              style: { resize: "none" },
            },
          } as ItemType,
          {
            name: "solutionWrapperEnd",
            col: { span: 24 },
            wrapperEnd: true,
          } as ItemType,
        ]
      : []),

    // ==================== 评估意见和意见说明（仅在 businessStatus=2 或 3 时显示） ====================
    ...(businessStatus === 2 || businessStatus === 3
      ? [
          {
            name: "assessmentSection",
            col: { span: 24 },
            custom: {
              render: () => (
                <div className={formStyles.subSectionTitle}>
                  {t("评估信息")}
                </div>
              ),
            },
          } as ItemType,
          {
            label: t("评估意见"),
            name: "assessmentOpinion",
            col: { span: 24 },
            labelCol: { span: 3 },
            wrapperCol: { span: 20 },
            className: "inlineLabel",
            rules: [{ required: true, message: t("请选择评估意见") }],
            widget: {
              widgetType: "Radio.Group",
              options: [
                { label: t("同意"), value: "agree" },
                { label: t("驳回"), value: "reject" },
              ],
              disabled: false,
            },
          } as ItemType,
          {
            label: t("意见说明"),
            name: "assessmentComments",
            col: { span: 24 },
            labelCol: { span: 3 },
            wrapperCol: { span: 22 },
            className: "inlineLabel",
            rules: [{ required: true, message: t("请输入意见说明") }],
            widget: {
              widgetType: "Input.TextArea",
              placeholder: t("请输入"),
              rows: 4,
              style: { resize: "none" },
              disabled: false,
            },
          } as ItemType,
        ]
      : []),

    {
      name: "supplierWrapperEnd",
      col: { span: 24 },
      wrapperEnd: true,
    },
  ];
};
